import type {
  CollectionId,
  DocumentId,
  DocumentVersionId,
} from "@superego/backend";
import { RouteName } from "@superego/routing";
import { useState } from "react";
import {
  PiClockCountdown,
  PiClockCountdownFill,
  PiFloppyDisk,
  PiInfo,
  PiTrash,
} from "react-icons/pi";
import { useIntl } from "react-intl";
import DataLoader from "../../../business-logic/backend/DataLoader.js";
import { useGlobalData } from "../../../business-logic/backend/GlobalData.js";
import {
  getDocumentQuery,
  listDocumentVersionsQuery,
} from "../../../business-logic/backend/hooks.js";
import useSaveShortcut from "../../../business-logic/forms/useSaveShortcut.js";
import useApplyAlwaysCollapsePrimarySidebar from "../../../business-logic/navigation/useApplyAlwaysCollapsePrimarySidebar.js";
import useNavigationState from "../../../business-logic/navigation/useNavigationState.js";
import classnames from "../../../utils/classnames.js";
import CollectionUtils from "../../../utils/CollectionUtils.js";
import DocumentUtils from "../../../utils/DocumentUtils.js";
import ContentSummaryPropertyValue from "../../design-system/ContentSummaryPropertyValue/ContentSummaryPropertyValue.js";
import RouteLevelErrors from "../../design-system/RouteLevelErrors/RouteLevelErrors.js";
import PanelHeaderActionSeparator from "../../design-system/Shell/PanelHeaderActionSeparator.js";
import Shell from "../../design-system/Shell/Shell.js";
import DeleteDocumentModalForm from "../../widgets/DeleteDocumentModalForm/DeleteDocumentModalForm.js";
import * as cs from "./Document.css.js";
import DocumentContent from "./DocumentContent.js";
import DocumentInfoModal from "./DocumentInfoModal.js";
import History from "./History/History.js";
import RedirectIfLatest from "./RedirectIfLatest.js";

interface Props {
  collectionId: CollectionId;
  documentId: DocumentId;
  documentVersionId?: DocumentVersionId;
}
export default function Document({
  collectionId,
  documentId,
  documentVersionId,
}: Props) {
  const intl = useIntl();
  const { navigateTo } = useNavigationState();
  const createFormId = `CreateNewDocumentVersionForm_${documentId}`;
  const [isCreateFormSubmitDisabled, setIsCreateFormSubmitDisabled] =
    useState(true);
  const { collections } = useGlobalData();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDocumentInfoModalOpen, setIsDocumentInfoModalOpen] = useState(false);
  const collection = CollectionUtils.findCollection(collections, collectionId);

  const defaultDocumentViewUiOptions =
    collection?.latestVersion.settings.defaultDocumentViewUiOptions ?? null;

  useSaveShortcut(createFormId, isCreateFormSubmitDisabled);
  useApplyAlwaysCollapsePrimarySidebar(
    defaultDocumentViewUiOptions?.alwaysCollapsePrimarySidebar,
  );

  return collection ? (
    <DataLoader
      queries={[
        getDocumentQuery([collection.id, documentId]),
        listDocumentVersionsQuery([collection.id, documentId]),
      ]}
      renderLoading={() => (
        <Shell.Panel slot="Main">
          <Shell.Panel.Header
            title={intl.formatMessage(
              { defaultMessage: "{collection}" },
              { collection: CollectionUtils.getDisplayName(collection) },
            )}
          />
        </Shell.Panel>
      )}
      renderErrors={(errors) => (
        <RouteLevelErrors
          headerTitle={intl.formatMessage(
            { defaultMessage: "{collection} » {documentId}" },
            {
              collection: CollectionUtils.getDisplayName(collection),
              documentId,
            },
          )}
          errors={errors}
        />
      )}
    >
      {(document, documentVersions) => {
        // History is shown when documentVersionId is specified.
        const isShowingHistory = typeof documentVersionId === "string";
        return (
          <Shell.Panel slot="Main">
            <RedirectIfLatest document={document} />
            <Shell.Panel.Header
              title={intl.formatMessage(
                { defaultMessage: "{collection} » {document}" },
                {
                  collection: CollectionUtils.getDisplayName(collection),
                  document: (
                    <ContentSummaryPropertyValue
                      value={DocumentUtils.getDisplayName(document)}
                    />
                  ),
                },
              )}
              actionsAriaLabel={intl.formatMessage({
                defaultMessage: "Document actions",
              })}
              actions={[
                {
                  label: intl.formatMessage({ defaultMessage: "History" }),
                  icon: isShowingHistory ? (
                    <PiClockCountdownFill />
                  ) : (
                    <PiClockCountdown />
                  ),
                  onPress: () =>
                    navigateTo({
                      name: RouteName.Document,
                      collectionId,
                      documentId,
                      documentVersionId: isShowingHistory
                        ? undefined
                        : document.latestVersion.id,
                    }),
                },
                {
                  label: intl.formatMessage({
                    defaultMessage: "Document info",
                  }),
                  icon: <PiInfo />,
                  onPress: () => setIsDocumentInfoModalOpen(true),
                },
                PanelHeaderActionSeparator,
                !isShowingHistory
                  ? {
                      label: intl.formatMessage({ defaultMessage: "Save" }),
                      icon: <PiFloppyDisk />,
                      submit: createFormId,
                      isDisabled: isCreateFormSubmitDisabled,
                    }
                  : null,
                {
                  label: intl.formatMessage({
                    defaultMessage: "Delete document",
                  }),
                  icon: <PiTrash />,
                  onPress: () => setIsDeleteModalOpen(true),
                  isDanger: true,
                },
              ]}
            />
            <Shell.Panel.Content
              fullWidth={
                isShowingHistory ||
                (defaultDocumentViewUiOptions?.fullWidth ?? false)
              }
              className={
                isShowingHistory ? cs.Document.historyLayout : undefined
              }
            >
              <div
                className={classnames(
                  cs.Document.contentWrapper.base,
                  isShowingHistory && cs.Document.contentWrapper.historyLayout,
                )}
              >
                <DocumentContent
                  collection={collection}
                  document={document}
                  documentVersions={documentVersions}
                  documentVersionId={
                    documentVersionId ?? document.latestVersion.id
                  }
                  formId={createFormId}
                  setSubmitDisabled={setIsCreateFormSubmitDisabled}
                  isShowingHistory={isShowingHistory}
                />
              </div>
              {isShowingHistory ? (
                <History
                  collection={collection}
                  document={document}
                  documentVersions={documentVersions}
                  className={cs.Document.history}
                />
              ) : null}
              <DeleteDocumentModalForm
                key={`DeleteDocumentModalForm_${documentId}`}
                collectionId={collectionId}
                documentId={documentId}
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onDeleted={() =>
                  navigateTo({ name: RouteName.Collection, collectionId })
                }
              />
              <DocumentInfoModal
                document={document}
                isOpen={isDocumentInfoModalOpen}
                onClose={() => setIsDocumentInfoModalOpen(false)}
              />
            </Shell.Panel.Content>
          </Shell.Panel>
        );
      }}
    </DataLoader>
  ) : null;
}
